import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getElementTypesBySquadPosition } from "core-integration/src/store/element-types/reducers";
import { IElementTypesBySquadPosition } from "core-integration/src/store/element-types/types";
import { updateElementTypeControl } from "core-integration/src/store/elements/thunks";
import { getFixturesForNextEventByTeam } from "core-integration/src/store/fixtures/reducers";
import { IFixturesForEventByTeam } from "core-integration/src/store/fixtures/types";
import { getProposedElements } from "core-integration/src/store/squad/reducers";
import { IProposedElements } from "core-integration/src/store/squad/types";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { ITeamsById } from "core-integration/src/store/teams/types";
import { integerToMoney } from "core-integration/src/utils/money";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import styled from "styled-components";
import ElementDialogButton from "../ElementDialogButton";
import ElementInTable from "../ElementInTable";
import { ElementCell, ElementRow, ElementTable } from "../ElementTable";
import { FixturesForElement } from "../FixtureForElement";
import Tooltip, { TooltipLabel } from "../Tooltip";

const ScrollWrapper = styled.div`
  overflow-x: auto;
  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    overflow-x: visible;
  }
`;

const FlexElementCell = styled(ElementCell)`
  display: flex;
  align-items: center;
`;

const FilterElTypeButton = styled.button`
  padding: 10px 0 9px;
  border: 0;
  background-color: transparent;
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.white1};
  white-space: normal;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const SquadListElement = styled.th`
  width: 40%;
`;

const SquadListCost = styled.th`
  width: 15%;
`;

const SquadListSelected = styled.th`
  width: 15%;
`;

const SquadListPoints = styled.th`
  width: 15%;
`;

const SquadListFixture = styled.th`
  width: 15%;
`;

interface ISquadListRowProps {
  elementTypesByPosition: IElementTypesBySquadPosition | null;
  fixturesForNextEventByTeam: IFixturesForEventByTeam;
  proposedElements: IProposedElements;
  pos: number;
  renderElementMenu: (position: number) => void;
  showElementType: (elementTypeId: number) => void;
  teamsById: ITeamsById;
}

const SquadListRow: React.FC<ISquadListRowProps> = ({
  elementTypesByPosition,
  proposedElements,
  pos,
  renderElementMenu,
  showElementType,
  teamsById,
  fixturesForNextEventByTeam,
}) => {
  const { t } = useTranslation();

  if (!elementTypesByPosition) {
    return null;
  }

  const element = proposedElements[pos];
  return (
    <ElementRow>
      {element ? (
        <>
          <FlexElementCell>
            <div>
              <ElementDialogButton elementId={element.id} variant="list" />
            </div>
            <ElementInTable
              renderElementMenu={() => renderElementMenu(pos)}
              element={element}
              team={teamsById[element.team]}
            />
          </FlexElementCell>
          <td>{integerToMoney(element.now_cost, 10)}</td>
          <td>{element.selected_by_percent}</td>
          <td>{element.total_points}</td>
          <td>
            <FixturesForElement
              delimiter={<br />}
              fixtures={fixturesForNextEventByTeam[element.team]}
              element={element}
              teamsById={teamsById}
            />
          </td>
        </>
      ) : (
        <>
          <td colSpan={5}>
            <FilterElTypeButton
              onClick={() =>
                showElementType(elementTypesByPosition[pos].thisType.id)
              }
            >
              {t("squad.selectElement", "Select {{position}}", {
                position: elementTypesByPosition[pos].thisType.singular_name,
              })}
            </FilterElTypeButton>
          </td>
        </>
      )}
    </ElementRow>
  );
};

interface IPropsFromState {
  elementTypesByPosition: IElementTypesBySquadPosition | null;
  fixturesForNextEventByTeam: IFixturesForEventByTeam;
  proposedElements: IProposedElements;
  teamsById: ITeamsById;
}

interface IPropsFromDispatch {
  showElementType: (elementTypeId: number) => void;
}

interface ISquadListTableProps {
  elementType: number;
  positions: number[];
  renderElementMenu: (pos: number) => void;
}

type Props = IPropsFromState & IPropsFromDispatch & ISquadListTableProps;

const SquadListTable: React.FC<Props> = ({
  elementType,
  elementTypesByPosition,
  positions,
  ...rest
}: Props) => {
  const { t } = useTranslation();

  if (!elementTypesByPosition) {
    return null;
  }
  return (
    <ScrollWrapper>
      <ElementTable elementType={elementType}>
        <thead>
          <tr>
            <SquadListElement>
              {elementTypesByPosition[positions[0]].thisType.plural_name}
            </SquadListElement>
            <SquadListCost>
              <Tooltip content={t("price", "Price")}>
                <TooltipLabel>M SAR</TooltipLabel>
              </Tooltip>
            </SquadListCost>
            <SquadListSelected>
              <Tooltip
                content={t("squadListTabel.selectedPct", "Teams selected by %")}
              >
                <TooltipLabel>
                  {t("squadListTabel.label.selectedPct", "VAL")}
                </TooltipLabel>
              </Tooltip>
            </SquadListSelected>
            <SquadListPoints>
              <Tooltip
                content={t("squadListTabel.totalPoints", "Total points")}
              >
                <TooltipLabel>
                  {t("squadListTabel.label.totalPoints", "TP")}
                </TooltipLabel>
              </Tooltip>
            </SquadListPoints>
            <SquadListFixture>
              <Tooltip
                content={t("squadListTabel.fixtureNext", "Fixture next")}
              >
                <TooltipLabel>
                  {t("squadListTabel.label.fixtureNext", "Mot")}
                </TooltipLabel>
              </Tooltip>
            </SquadListFixture>
          </tr>
        </thead>
        <tbody>
          {positions.map((p) => (
            <SquadListRow
              key={p}
              pos={p}
              elementTypesByPosition={elementTypesByPosition}
              {...rest}
            />
          ))}
        </tbody>
      </ElementTable>
    </ScrollWrapper>
  );
};

const mapStateToProps = (state: RootState): IPropsFromState => ({
  elementTypesByPosition: getElementTypesBySquadPosition(state),
  fixturesForNextEventByTeam: getFixturesForNextEventByTeam(state),
  proposedElements: getProposedElements(state),
  teamsById: getTeamsById(state),
});

const mapDispatchToProps = (dispatch: ThunkDispatch): IPropsFromDispatch => ({
  showElementType: (elementTypeId) =>
    dispatch(updateElementTypeControl(elementTypeId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SquadListTable);
